import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, X } from "lucide-react";
import { CATEGORIES, PRODUCTS } from "@/data/products";

export const Collections = () => {
  const [active, setActive] = useState(null);

  const closeModal = () => setActive(null);
  const activeCat = CATEGORIES.find((c) => c.slug === active);
  const activeProducts = active ? PRODUCTS.filter((p) => p.category === active) : [];

  return (
    <section id="koleksiyonlar" className="py-24 lg:py-32 beige-soft">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="eyebrow">Kategoriler</span>
          <h2 className="font-display text-4xl lg:text-5xl mt-3 text-[hsl(var(--nl-anthracite))]">
            <em className="not-italic font-serif italic gold-text">Koleksiyonlar</em>
          </h2>
          <div className="gold-line mt-5 mx-auto" />
          <p className="mt-6 text-sm text-[hsl(var(--muted-foreground))]">
            Aşağıdaki kategorilerden tarzınıza uygun parçaları keşfedin.
            Ürünlere tıklayarak Shopier üzerinden sipariş verebilirsiniz.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {CATEGORIES.map((c, i) => (
            <motion.button
              key={c.slug}
              data-testid={`category-${c.slug}`}
              onClick={() => setActive(c.slug)}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: 0.05 * i }}
              className="group relative overflow-hidden text-left bg-white border border-[hsl(var(--nl-line))] hover:border-[hsl(var(--nl-champagne))] transition-all duration-500"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={c.image}
                  alt={c.title}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--nl-anthracite)/0.6)] via-transparent to-transparent" />
                <span className="absolute top-4 left-4 eyebrow text-white/90">0{i + 1}</span>
                <span className="absolute top-4 right-4 px-2.5 py-1 bg-[hsl(var(--nl-champagne))] text-[hsl(var(--nl-anthracite))] text-[10px] tracking-[0.2em] uppercase font-semibold">
                  {c.count} Ürün
                </span>
              </div>
              <div className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="font-display text-2xl text-[hsl(var(--nl-anthracite))]">{c.title}</h3>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5">{c.desc}</p>
                </div>
                <ArrowRight size={18} className="gold-text transition-transform duration-500 group-hover:translate-x-1" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && activeCat && (
          <motion.div
            data-testid="category-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 bg-[hsl(var(--nl-anthracite)/0.7)] backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={closeModal}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
              className="bg-[hsl(var(--nl-cream))] w-full max-w-6xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-[hsl(var(--nl-cream))] z-10 flex items-center justify-between px-6 py-5 border-b border-[hsl(var(--nl-line))]">
                <div>
                  <span className="eyebrow">Koleksiyon · {activeProducts.length} ürün</span>
                  <h3 className="font-display text-2xl lg:text-3xl text-[hsl(var(--nl-anthracite))] mt-1">{activeCat.title}</h3>
                </div>
                <button
                  data-testid="close-category-modal"
                  onClick={closeModal}
                  className="w-10 h-10 grid place-items-center border border-[hsl(var(--nl-line))] hover:border-[hsl(var(--nl-anthracite))] transition-colors"
                  aria-label="Kapat"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-5 lg:p-6">
                {activeProducts.length === 0 ? (
                  <p className="text-center py-12 text-[hsl(var(--muted-foreground))]">
                    Bu kategoride yakında yeni ürünlerimiz olacak.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
                    {activeProducts.map((p) => (
                      <a
                        key={p.id}
                        href={p.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`modal-product-${p.id}`}
                        className="product-card group block"
                      >
                        <div className="relative aspect-square overflow-hidden bg-[hsl(var(--nl-beige))]">
                          <img src={p.image} alt={p.name} className="product-img w-full h-full object-cover" loading="lazy" />
                          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--nl-anthracite)/0.4)] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                          <div className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-[hsl(var(--nl-champagne))] text-[hsl(var(--nl-anthracite))] grid place-items-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                            <ArrowUpRight size={14} />
                          </div>
                        </div>
                        <div className="p-3">
                          <h4 className="font-serif text-sm text-[hsl(var(--nl-anthracite))] leading-snug line-clamp-2 min-h-[2.4rem]">{p.name}</h4>
                          <div className="mt-2 flex items-center justify-between">
                            {p.price ? (
                              <span className="font-display text-base gold-text">{p.price}</span>
                            ) : (
                              <span className="text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--muted-foreground))]">Detay</span>
                            )}
                            <span className="text-[9px] uppercase tracking-[0.18em] gold-text">Al →</span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Collections;
